import React, { useState } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useLanguage } from '../contexts/LanguageContext';

export const Layout: React.FC = () => {
  const { logout } = useAuth();
  const { t, language, setLanguage } = useLanguage();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  const handleLogout = () => {
    logout();
    setIsMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-background-100 flex flex-col">
      {/* Навигация */}
      <nav className="bg-white border-b border-background-300 shadow-sm">
        <div className="container-app py-3">
          <div className="flex items-center justify-between">
            {/* Логотип */}
            <div className="flex items-center space-x-3">
              <h1 className="text-xl font-bold text-text-primary">Restaurant Orders</h1>
            </div>

            {/* Десктоп меню */}
            <div className="hidden lg:flex items-center gap-6">
              {/* Навигационные ссылки */}
              <div className="flex gap-2">
                <Link
                  to="/orders"
                  className={isActive('/orders') ? 'nav-link-active' : 'nav-link'}
                >
                  📋 {t('nav.orders')}
                </Link>
                <Link
                  to="/menu"
                  className={isActive('/menu') ? 'nav-link-active' : 'nav-link'}
                >
                  🍽️ {t('nav.menu')}
                </Link>
                <Link
                  to="/promo-codes"
                  className={isActive('/promo-codes') ? 'nav-link-active' : 'nav-link'}
                >
                  🎫 {t('nav.promoCodes')}
                </Link>
                <Link
                  to="/employees"
                  className={isActive('/employees') ? 'nav-link-active' : 'nav-link'}
                >
                  👥 {t('nav.employees')}
                </Link>
                <Link
                  to="/battleship"
                  className={isActive('/battleship') ? 'nav-link-active' : 'nav-link'}
                >
                  ⚓ {t('nav.battleship')}
                </Link>
              </div>

              {/* Пользователь и действия */}
              <div className="flex items-center gap-2 ml-4 pl-4 border-l border-background-300">
                {/* Информация об администраторе */}
                <span className="text-sm text-text-secondary hidden xl:block">
                  {t('nav.administrator')}: admin
                </span>

                {/* Переключатель языка */}
                <div className="flex items-center gap-1 bg-background-200 rounded-lg p-1">
                  <button
                    onClick={() => setLanguage('ru')}
                    className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                      language === 'ru'
                        ? 'bg-white text-text-primary shadow-sm'
                        : 'text-text-secondary hover:text-text-primary'
                    }`}
                  >
                    RU
                  </button>
                  <button
                    onClick={() => setLanguage('en')}
                    className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                      language === 'en'
                        ? 'bg-white text-text-primary shadow-sm'
                        : 'text-text-secondary hover:text-text-primary'
                    }`}
                  >
                    EN
                  </button>
                </div>

                {/* Кнопка выхода */}
                <button onClick={handleLogout} className="btn btn-secondary btn-sm">
                  {t('nav.logout')}
                </button>
              </div>
            </div>

            {/* Мобильное меню - кнопка */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden btn btn-secondary"
              aria-label="Toggle menu"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                {isMobileMenuOpen ? (
                  <path d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>

          {/* Мобильное меню - выпадающее */}
          {isMobileMenuOpen && (
            <div className="lg:hidden mt-4 pt-4 border-t border-background-300">
              <div className="flex flex-col gap-2">
                {/* Навигационные ссылки */}
                <Link
                  to="/orders"
                  className={isActive('/orders') ? 'nav-link-active' : 'nav-link'}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  📋 {t('nav.orders')}
                </Link>
                <Link
                  to="/menu"
                  className={isActive('/menu') ? 'nav-link-active' : 'nav-link'}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  🍽️ {t('nav.menu')}
                </Link>
                <Link
                  to="/promo-codes"
                  className={isActive('/promo-codes') ? 'nav-link-active' : 'nav-link'}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  🎫 {t('nav.promoCodes')}
                </Link>
                <Link
                  to="/employees"
                  className={isActive('/employees') ? 'nav-link-active' : 'nav-link'}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  👥 {t('nav.employees')}
                </Link>
                <Link
                  to="/battleship"
                  className={isActive('/battleship') ? 'nav-link-active' : 'nav-link'}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  ⚓ {t('nav.battleship')}
                </Link>

                {/* Разделитель */}
                <div className="border-t border-background-300 my-2"></div>

                {/* Информация об администраторе */}
                <div className="px-4 py-2 text-sm text-text-secondary">
                  {t('nav.administrator')}: admin
                </div>

                {/* Переключатель языка */}
                <div className="px-4 py-2">
                  <div className="flex items-center gap-1 bg-background-200 rounded-lg p-1 w-full">
                    <button
                      onClick={() => setLanguage('ru')}
                      className={`flex-1 px-3 py-2 rounded text-sm font-medium transition-colors ${
                        language === 'ru'
                          ? 'bg-white text-text-primary shadow-sm'
                          : 'text-text-secondary hover:text-text-primary'
                      }`}
                    >
                      RU
                    </button>
                    <button
                      onClick={() => setLanguage('en')}
                      className={`flex-1 px-3 py-2 rounded text-sm font-medium transition-colors ${
                        language === 'en'
                          ? 'bg-white text-text-primary shadow-sm'
                          : 'text-text-secondary hover:text-text-primary'
                      }`}
                    >
                      EN
                    </button>
                  </div>
                </div>

                {/* Кнопка выхода */}
                <button onClick={handleLogout} className="btn btn-secondary w-full">
                  {t('nav.logout')}
                </button>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Основной контент */}
      <main className="flex-1">
        <Outlet />
      </main>

      {/* Футер */}
      <footer className="bg-white border-t border-background-300 mt-auto">
        <div className="container-app py-8">
          <div className="text-center">
            <p className="text-text-tertiary text-sm">
              © 2025 Restaurant Orders
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};
